import GAME_EVENT_FORM_BROADCAST_FIELDS from '../../constants/gameEventFormBroadcastFields'
import isCourtLivestreamEnabled from '../isCourtLivestreamEnabled'
import LIVESTREAM_FIELDS_VALIDATIONS from '../../constants/livestreamFieldsValidations'
import { STREAM_FEATURES, supports } from '../../constants/streamTypeCapabilities'
import ensureFullRtmpDestination from './ensureFullRtmpDestination'

// Converts logo form values to the convention expected by the backend:
//   null        → no change (preserve existing logo)
//   ""          → explicit removal
//   "data:..."  → new upload
//   "http(s)://..." → existing CDN URL (passed through for Load Recent Setup / create flows)
const transformLogoField = (value) => {
  if (value === '') return ''
  if (value && value.startsWith('data:')) return value
  if (value && (value.startsWith('http://') || value.startsWith('https://'))) return value
  return null
}

const parseGameEventFormSubmit = (data, courts, selectedEventType, mediaIsUploading) => {
  const {
    [GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST]: isBroadcast,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD]: isRecordVideo,
  } = data || {}

  // Operators were only ever valid on Plus broadcast games, and Studio has moved to the Pro
  // tier, so no type accepts them now. Read the raw input values (before the returned object
  // normalizes type/isRecord) so a stale operator id is dropped when the user switches stream
  // type or toggles broadcast off.
  const canAssignStudioOperator =
    supports(data[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE], STREAM_FEATURES.STUDIO) && isBroadcast

  const eventTeams =
    selectedEventType.name === 'Game'
      ? [
          {
            teamId: data.homeTeam?.gender ? data.homeTeam.id : data.homeTeam.teamId || null,
            threeLetterIdentifier: data.homeTeam.threeLetterIdentifier || null,
            name: data.homeTeam.name,
            logoDataUri: transformLogoField(data.homeTeamLogoDataUri),
            // isHomeTeam is flag-driven by homeTeamIndex (non-swapping slot model), not by
            // array position. Exactly one slot is always home.
            isHomeTeam: data[GAME_EVENT_FORM_BROADCAST_FIELDS.HOME_TEAM_INDEX] === 0,
          },
          {
            teamId: data.awayTeam?.gender?.id ? data.awayTeam.id : data.awayTeam.teamId || null,
            threeLetterIdentifier: data.awayTeam.threeLetterIdentifier || null,
            name: data.awayTeam.name,
            logoDataUri: transformLogoField(data.awayTeamLogoDataUri),
            isHomeTeam: data[GAME_EVENT_FORM_BROADCAST_FIELDS.HOME_TEAM_INDEX] === 1,
          },
        ]
      : data.teams.map((team) => ({
          teamId: team.id || team.teamId || null,
          threeLetterIdentifier: team.threeLetterIdentifier || null,
          name: team.name || team,
          logoUrl: team.logoUrl || team.teamLogo || null,
        }))
  const isCourtLivestream = isCourtLivestreamEnabled(courts, data.courtId)
  const isEventLivestream = isBroadcast || isRecordVideo

  const checkFieldValidation = (fieldName, validationRule, typeOfEmptyValueToReturn) => {
    const { [fieldName]: fieldValue } = data || {}
    if (!isCourtLivestream || !isEventLivestream || (validationRule && !validationRule(data)))
      return typeOfEmptyValueToReturn !== undefined ? typeOfEmptyValueToReturn : ''

    return fieldValue
  }

  const getGameStateTrackingType = (autoTracking, manualTracking) => {
    if (autoTracking) return 'Auto'
    if (manualTracking) return 'Manual'
    return 'Disabled'
  }

  return {
    ...data,
    eventTeams: eventTeams,
    showCards: false,
    autofollowEnabled: checkFieldValidation(
      'autofollowEnabled',
      (values) =>
        LIVESTREAM_FIELDS_VALIDATIONS[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_LIVESTREAM_TYPE_FULL](values) ||
        LIVESTREAM_FIELDS_VALIDATIONS[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_LIVESTREAM_TYPE_LITE](values),
      false,
    ),
    externalId: data.hasStats ? data.externalId : null,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD]: isEventLivestream,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE]: checkFieldValidation(
      GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE,
      LIVESTREAM_FIELDS_VALIDATIONS[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE],
    ),
    [GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION]: ensureFullRtmpDestination(
      checkFieldValidation(
        GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION,
        LIVESTREAM_FIELDS_VALIDATIONS[GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION],
        null,
      ),
    ),
    [GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG]: checkFieldValidation(
      GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG,
      LIVESTREAM_FIELDS_VALIDATIONS[GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG],
      null,
    ),
    [GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION_2]: ensureFullRtmpDestination(
      checkFieldValidation(
        GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION_2,
        LIVESTREAM_FIELDS_VALIDATIONS[GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION_2],
        null,
      ),
    ),
    // Livestream-only, like remote commentary (Studio): a record-only game is not commentated.
    [GAME_EVENT_FORM_BROADCAST_FIELDS.IS_COMMENTARY_ON]:
      Boolean(isBroadcast) &&
      supports(data[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE], STREAM_FEATURES.LOCAL_COMMENTARY) &&
      Boolean(data[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_COMMENTARY_ON]),
    // On-site commentary at the court; like Studio itself, livestream-only.
    [GAME_EVENT_FORM_BROADCAST_FIELDS.COMMENTARY_MODE]:
      Boolean(isBroadcast) &&
      supports(data[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE], STREAM_FEATURES.STUDIO) &&
      data[GAME_EVENT_FORM_BROADCAST_FIELDS.COMMENTARY_MODE] === 'OnSite'
        ? 'OnSite'
        : 'OffSite',
    // Only ad-inserting livestreams carry the flag (a recording plays no ads); for the rest the
    // backend stores null regardless.
    [GAME_EVENT_FORM_BROADCAST_FIELDS.SHARED_REVENUE]:
      isBroadcast &&
      supports(data[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE], STREAM_FEATURES.AD_INSERTION)
        ? Boolean(data[GAME_EVENT_FORM_BROADCAST_FIELDS.SHARED_REVENUE])
        : null,
    // Only a streamed event has a scoreboard to feed. Null is not "no scoreboard" but "no opinion":
    // the backend then applies its own default, which is what an event created without ever opening
    // the streaming tab should get.
    [GAME_EVENT_FORM_BROADCAST_FIELDS.SCOREBOARD_SOURCE]: checkFieldValidation(
      GAME_EVENT_FORM_BROADCAST_FIELDS.SCOREBOARD_SOURCE,
      null,
      null,
    ),
    [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_COMPETITION_LOGO]: transformLogoField(
      checkFieldValidation(
        GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_COMPETITION_LOGO,
        LIVESTREAM_FIELDS_VALIDATIONS[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_LIVESTREAM_TYPE_FULL],
      ),
    ),
    [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_TOP_LEFT_LOGO]: transformLogoField(
      checkFieldValidation(
        GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_TOP_LEFT_LOGO,
        LIVESTREAM_FIELDS_VALIDATIONS[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_LIVESTREAM_TYPE_FULL],
      ),
    ),
    [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_TOP_RIGHT_LOGO]: transformLogoField(
      checkFieldValidation(
        GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_TOP_RIGHT_LOGO,
        LIVESTREAM_FIELDS_VALIDATIONS[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_LIVESTREAM_TYPE_FULL],
      ),
    ),
    [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_BOTTOM_LEFT_LOGO]: transformLogoField(
      checkFieldValidation(
        GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_BOTTOM_LEFT_LOGO,
        LIVESTREAM_FIELDS_VALIDATIONS[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_LIVESTREAM_TYPE_FULL],
      ),
    ),
    [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_BOTTOM_RIGHT_LOGO]: transformLogoField(
      checkFieldValidation(
        GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_BOTTOM_RIGHT_LOGO,
        LIVESTREAM_FIELDS_VALIDATIONS[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_LIVESTREAM_TYPE_FULL],
      ),
    ),
    [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_CENTER_LOGO]: transformLogoField(
      checkFieldValidation(
        GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_CENTER_LOGO,
        LIVESTREAM_FIELDS_VALIDATIONS[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_LIVESTREAM_TYPE_FULL],
      ),
    ),
    [GAME_EVENT_FORM_BROADCAST_FIELDS.UPLOADED_MEDIAS]: !mediaIsUploading
      ? checkFieldValidation(
          GAME_EVENT_FORM_BROADCAST_FIELDS.UPLOADED_MEDIAS,
          LIVESTREAM_FIELDS_VALIDATIONS[GAME_EVENT_FORM_BROADCAST_FIELDS.UPLOADED_MEDIAS],
        ) || {}
      : {},
    GameStateTrackingType: getGameStateTrackingType(
      data[GAME_EVENT_FORM_BROADCAST_FIELDS.GAME_STATE_TRACKING_AUTO],
      data[GAME_EVENT_FORM_BROADCAST_FIELDS.GAME_STATE_TRACKING_MANUAL]
    ),
    studioOperatorIds: canAssignStudioOperator && data.studioOperatorId ? [data.studioOperatorId] : [],
    statisticianIds: data.hasStats && data.statisticianIds?.length ? data.statisticianIds : [],
    videoOperatorIds: data.videoOperatorIds ?? [],
  }
}

export default parseGameEventFormSubmit
